import React, { Suspense, useState } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { essentialLocales, setAppLocales, i18n } from '@fs/zion-locale'
import Root from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { AppLayout } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'

import { FeatureFlagsProvider } from '@fs/zion-flags'
import { NoticeLoading } from '@fs/zion-icon'
import { GroupsProvider } from '@fs/zion-tree-groups'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Enable 30 essential locales
setAppLocales(essentialLocales)

// This is where you pass data from the server to the client using the SERVER_DATA global.
// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const FrontierRoot = () => {
  const [showFooter, setShowFooter] = useState(true)
  const [fatFooter, setFatFooter] = useState(false)
  const [showRestoreCommunityBanner, setShowRestoreCommunityBanner] = useState(false)

  const alertBannerProps = {
    message: i18n.t(
      'memories.restore.community-group-banner.message',
      'You can join a private FamilySearch Community group to provide feedback, ask questions, and share tips.'
    ),
    confirmText: i18n.t('memories.restore.community-group-banner.confirm', 'Go to community'),
    type: 'info',
    onConfirm: () => {
      trackEvent({ event_name: 'Button:Community Banner:Go to Community:Click' })
      window.open('https://community.familysearch.org/en/group/322-memories-restoration-tool')
    },
    open: showRestoreCommunityBanner,
    setOpen: setShowRestoreCommunityBanner,
  }

  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FeatureFlagsProvider mockFlags={mockFlags}>
          <Router basename={base}>
            <Root
              analytics
              footer={{ hidden: !showFooter, useFatFooter: fatFooter }}
              alertBannerProps={alertBannerProps}
              fullScreen
            >
              <AppLayout fullWidth>
                <GroupsProvider>
                  <App
                    setShowFooter={setShowFooter}
                    setFatFooter={setFatFooter}
                    setShowRestoreCommunityBanner={setShowRestoreCommunityBanner}
                  />
                </GroupsProvider>
              </AppLayout>
            </Root>
          </Router>
        </FeatureFlagsProvider>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
